type Entry = {
  video: HTMLVideoElement; ratio: number; distance: number; userPaused: boolean
  blocked: boolean; pending: boolean; retryAfterPending: boolean; expectedPauses: number; generation: number
}

const owners = new WeakMap<HTMLVideoElement, Entry>()

// Browser autoplay fallback is not an explicit choice to mute later clips.
export function createVideoSoundPreference(initial = true) {
  let enabled = initial
  return { isEnabled: () => enabled, setEnabled(value: boolean) { enabled = value } }
}
export const videoSoundPreference = createVideoSoundPreference()
const soundSnapshots = new WeakMap<HTMLVideoElement, { muted: boolean; volume: number }>()

export function setVideoSound(video: HTMLVideoElement, enabled: boolean) {
  video.muted = !enabled
  if (enabled && video.volume === 0) video.volume = 1
  soundSnapshots.set(video, { muted: video.muted, volume: video.volume })
}

export function rememberVideoSoundChange(video: HTMLVideoElement) {
  const previous = soundSnapshots.get(video)
  const current = { muted: video.muted, volume: video.volume }
  if (previous && (previous.muted !== current.muted || previous.volume !== current.volume)) {
    videoSoundPreference.setEnabled(!current.muted && current.volume > 0)
  }
  soundSnapshots.set(video, current)
  return current.muted || current.volume === 0
}

// One coordinator prevents two videos from competing when both are visible.
export function createViewportVideoController(videos: HTMLVideoElement[], sound?: ReturnType<typeof createVideoSoundPreference>) {
  const entries = videos.map(video => ({ video, ratio: 0, distance: Infinity,
    userPaused: false, blocked: false, pending: false, retryAfterPending: false, expectedPauses: 0, generation: 0 } as Entry))
  entries.forEach(entry => owners.set(entry.video, entry))
  let active: Entry | undefined
  let fullscreen: Entry | undefined
  let pageVisible = true
  let reducedMotion = false
  let disposed = false

  function pause(entry: Entry) {
    if (owners.get(entry.video) === entry && !entry.video.paused) {
      entry.expectedPauses++
      entry.video.pause()
    }
  }

  function pauseOthers(chosen?: Entry) {
    entries.forEach(entry => { if (entry !== chosen) pause(entry) })
  }

  function eligible(entry: Entry) {
    return !disposed && pageVisible && !reducedMotion && (entry === fullscreen || entry.ratio >= .5)
      && !entry.userPaused && !entry.blocked
  }

  function onscreen(entry: Entry) { return entry === fullscreen || entry.ratio >= .2 }

  async function start(entry: Entry) {
    if (entry.pending) { entry.retryAfterPending = true; return }
    if (!entry.video.paused) return
    entry.pending = true
    entry.retryAfterPending = false
    const generation = entry.generation
    if (sound) setVideoSound(entry.video, sound.isEnabled())
    try {
      await entry.video.play()
    } catch (reason) {
      if (disposed || generation !== entry.generation) return
      // A previously unmuted clip can be blocked when re-entering on mobile.
      if (active === entry && eligible(entry) && !entry.video.muted
        && (reason as { name?: string })?.name === 'NotAllowedError') {
        setVideoSound(entry.video, false)
        try { await entry.video.play() } catch { if (generation === entry.generation) entry.blocked = true }
      } else if ((reason as { name?: string })?.name !== 'AbortError') {
        entry.blocked = true
      }
    } finally {
      entry.pending = false
      if (disposed || active !== entry || !pageVisible || !onscreen(entry)) pause(entry)
      // Re-entry can happen before a scroll-triggered AbortError settles.
      // Retry only when another visibility update requested it, never in a loop.
      if (entry.retryAfterPending && active === entry && eligible(entry)) void start(entry)
    }
  }

  function reconcile() {
    if (disposed) return
    if (!pageVisible) { active = undefined; pauseOthers(); return }
    if (active && !onscreen(active)) { pause(active); active = undefined }
    if (fullscreen) {
      active = fullscreen; pauseOthers(fullscreen)
      if (eligible(fullscreen)) void start(fullscreen)
      return
    }
    // The OS motion preference disables automatic starts, not manual playback.
    if (reducedMotion) return
    const candidates = entries.filter(eligible).sort((a, b) => b.ratio - a.ratio || a.distance - b.distance)
    let chosen = candidates[0]
    if (active && eligible(active) && chosen && active.ratio + .15 >= chosen.ratio) chosen = active
    if (!chosen) return
    active = chosen
    pauseOthers(chosen)
    void start(chosen)
  }

  const cleanups = entries.map(entry => {
    const onPlay = () => {
      if (entry.pending) {
        if (active !== entry || !pageVisible || !onscreen(entry)) pause(entry)
        return
      }
      if (!pageVisible || !onscreen(entry)) { pause(entry); return }
      // Native controls or the sound/play button are deliberate user actions.
      entry.userPaused = false
      entry.blocked = false
      active = entry
      pauseOthers(entry)
    }
    const onPause = () => {
      if (entry.expectedPauses > 0) { entry.expectedPauses--; return }
      // A queued native pause may arrive after scrolling out reset the session.
      if (!entry.video.ended && onscreen(entry)) entry.userPaused = true
    }
    entry.video.addEventListener('play', onPlay)
    entry.video.addEventListener('pause', onPause)
    return () => { entry.video.removeEventListener('play', onPlay); entry.video.removeEventListener('pause', onPause) }
  })

  return {
    updateVisibility(updates: { video: HTMLVideoElement; ratio: number; distance: number }[]) {
      for (const update of updates) {
        const entry = entries.find(item => item.video === update.video)
        if (!entry) continue
        if (entry.ratio >= .2 && update.ratio < .2 && entry !== fullscreen) entry.generation++
        entry.ratio = update.ratio
        entry.distance = update.distance
        if (!onscreen(entry)) { entry.userPaused = false; entry.blocked = false; pause(entry) }
      }
      reconcile()
    },
    setPageVisible(value: boolean) {
      if (pageVisible && !value) entries.forEach(entry => entry.generation++)
      pageVisible = value; reconcile()
    },
    setFullscreen(video?: HTMLVideoElement) {
      const previous = fullscreen
      fullscreen = entries.find(entry => entry.video === video)
      if (previous && previous !== fullscreen && previous.ratio < .2) {
        previous.generation++; previous.userPaused = false; previous.blocked = false
      }
      reconcile()
    },
    setReducedMotion(value: boolean) {
      reducedMotion = value
      if (value) { active = undefined; pauseOthers() } else reconcile()
    },
    destroy() {
      disposed = true
      cleanups.forEach(cleanup => cleanup())
      pauseOthers()
    },
  }
}

// Measure coverage of what can fit on screen, including short landscape phones.
export function videoViewportCoverage(rect: { width: number; height: number }, intersection: { width: number; height: number }, viewport: { width: number; height: number }) {
  const available = Math.min(rect.width, viewport.width) * Math.min(rect.height, viewport.height)
  return available > 0 ? Math.min(1, intersection.width * intersection.height / available) : 0
}

export function installViewportVideoPlayback(doc: Document = document) {
  const videos = Array.from(doc.querySelectorAll<HTMLVideoElement>('video[data-viewport-video]'))
  const controller = createViewportVideoController(videos, videoSoundPreference)
  const canObserve = typeof IntersectionObserver !== 'undefined'
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
  const syncMotion = () => controller.setReducedMotion(preference.matches || !canObserve)
  const syncVisibility = () => controller.setPageVisible(!doc.hidden)
  syncMotion()
  syncVisibility()
  preference.addEventListener('change', syncMotion)
  doc.addEventListener('visibilitychange', syncVisibility)
  const syncFullscreen = () => controller.setFullscreen(videos.find(video => doc.fullscreenElement?.contains(video)))
  doc.addEventListener('fullscreenchange', syncFullscreen)
  const nativeFullscreenCleanups = videos.map(video => {
    const begin = () => controller.setFullscreen(video)
    const end = () => controller.setFullscreen()
    video.addEventListener('webkitbeginfullscreen', begin)
    video.addEventListener('webkitendfullscreen', end)
    return () => { video.removeEventListener('webkitbeginfullscreen', begin); video.removeEventListener('webkitendfullscreen', end) }
  })
  const observer = !canObserve ? null : new IntersectionObserver(changes => {
    controller.updateVisibility(changes.map(change => ({ video: change.target as HTMLVideoElement,
      ratio: change.isIntersecting ? videoViewportCoverage(change.boundingClientRect, change.intersectionRect,
        change.rootBounds ?? { width: window.innerWidth, height: window.innerHeight }) : 0,
      distance: Math.abs(change.boundingClientRect.top + change.boundingClientRect.height / 2 - window.innerHeight / 2),
    })))
  }, { threshold: Array.from({ length: 41 }, (_, index) => index / 40) })
  // Warm the next clip without starting it or downloading every full video.
  const warmup = !canObserve ? null : new IntersectionObserver(changes => {
    changes.forEach(change => {
      if (!change.isIntersecting) return
      const video = change.target as HTMLVideoElement
      video.preload = 'auto'
      warmup?.unobserve(video)
    })
  }, { rootMargin: '500px 0px' })
  videos.forEach(video => observer?.observe(video))
  videos.forEach(video => warmup?.observe(video))
  if (!observer) controller.updateVisibility(videos.map(video => ({ video, ratio: 1, distance: 0 })))
  return () => {
    observer?.disconnect()
    warmup?.disconnect()
    preference.removeEventListener('change', syncMotion)
    doc.removeEventListener('visibilitychange', syncVisibility)
    doc.removeEventListener('fullscreenchange', syncFullscreen)
    nativeFullscreenCleanups.forEach(cleanup => cleanup())
    controller.destroy()
  }
}
